import { ReactNode } from 'react'
import { useTranslation } from 'libs/i18n'
import { format, parseISO } from 'libs/dates'
import { useUserRoles } from 'shared/hooks'
import { ActivityStatus, BookingStatus, PaymentStatus } from 'features/bookings'
import type { Booking } from 'features/bookings/types/bookings.types'
import type { RentalItem } from 'features/rentals'
import { useBookingOptions } from 'features/bookings/hooks/useBookingOptions'

type BookingSummaryProps = {
	booking: Booking
}

const date = (value: string | Date) => format(new Date(value), 'dd.MM.yyyy')

const SummaryItem = ({ label, value }: { label: string; value: ReactNode }) => (
	<div className="min-w-0">
		<dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
			{label}
		</dt>
		<dd className="mt-0.5 truncate text-[12px] font-medium tabular-nums text-foreground">
			{value}
		</dd>
	</div>
)

export const BookingSummary = ({ booking }: BookingSummaryProps) => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const { rentals } = useBookingOptions({ daysLabel: t('days') })
	const rental =
		typeof booking.rental === 'object'
			? (booking.rental as RentalItem)
			: rentals.items.find((item) => item.id === booking.rental)
	const days =
		rental?.duration ??
		Math.max(
			0,
			Math.round(
				(new Date(booking.end_date).getTime() - new Date(booking.start_date).getTime()) / 86400000
			)
		)

	return (
		<section className="rounded-lg border border-border bg-card p-5 shadow-panel sm:p-6">
			<div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<span className="font-display text-xl font-bold tabular-nums text-primary">
							#{booking.ref_nr}
						</span>
						{isStaff && (
							<ActivityStatus
								uppercase={false}
								dotSize="sm"
								status={booking.is_active}
								prefix={t('Status')}
							/>
						)}
						<BookingStatus
							uppercase={false}
							dotSize="sm"
							status={booking.status}
							prefix={t('Booking status')}
						/>
						<PaymentStatus dotSize="sm" status={booking.payment?.status} prefix={t('Payment')} />
					</div>
					<div className="mt-2 text-[14px] font-semibold text-foreground">
						{booking.user?.name || booking.user?.email}
					</div>
					<div className="text-[12px] text-muted-foreground">
						{booking.user?.email}
						{booking.booth?.booth_type?.name ? ` · ${booking.booth.booth_type.name}` : ''}
					</div>
				</div>
				<dl className="grid shrink-0 grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3 xl:w-[420px]">
					<SummaryItem
						label={t('Area')}
						value={booking.booth?.number ? `#${booking.booth.number}` : '—'}
					/>
					<SummaryItem label={t('Days')} value={days} />
					<SummaryItem label={t('From')} value={date(booking.start_date)} />
					<SummaryItem label={t('To')} value={date(booking.end_date)} />
					<SummaryItem
						label={t('Created')}
						value={format(parseISO(String(booking.created)), 'dd.MM.yyyy')}
					/>
					<SummaryItem label={t('Method')} value={booking.payment?.method || '—'} />
				</dl>
			</div>
		</section>
	)
}
